import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createTachographCard,
  deleteTachographCard,
  getTachographCardById,
  getTachographCardColumns,
  getTachographCards,
  updateTachographCard,
  type CreateTachographCardPayload,
  type TachographCardListParams,
  type UpdateTachographCardPayload,
} from "src/services/tachographCardService";
import { getTachographCompanyLookup } from "src/services/tachographCompanyService";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetTachographCards = (params: TachographCardListParams) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.tachographCardList, params, language],
      queryFn: () => getTachographCards(params, language),
      refetchOnMount: true,
      retry: false,
    }),
    true
  );
};

export const useQueryGetTachographCardColumns = () => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tachographCardColumns, language],
    queryFn: () => getTachographCardColumns(language),
    retry: false,
  });
};

export const useQueryGetTachographCardById = (cardId: string | null, enabled: boolean) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.tachographCardDetail, cardId, language],
    queryFn: () => getTachographCardById(cardId!, language),
    enabled: enabled && Boolean(cardId),
    retry: false,
    staleTime: 0,
  });
};

export const useMutationCreateTachographCard = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: CreateTachographCardPayload) => createTachographCard(payload, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tachographCardList,
      }),
  });
};

export const useMutationUpdateTachographCard = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: UpdateTachographCardPayload) => updateTachographCard(payload, language),
    onSuccess: (_response, payload) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: QueryNames.tachographCardList,
        }),
        queryClient.invalidateQueries({
          queryKey: [...QueryNames.tachographCardDetail, payload.cardId],
        }),
      ]),
  });
};

export const useMutationDeleteTachographCard = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (cardId: string) => deleteTachographCard(cardId, language),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: QueryNames.tachographCardList,
      }),
  });
};

export const useQueryGetTachographCompanyLookup = (enabled: boolean) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: ["tachographCompanyLookup", language],
    queryFn: () => getTachographCompanyLookup(language),
    enabled,
    retry: false,
  });
};
